import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import type {
  AIActionApprovalRef,
  AIActionProposal,
  AIChatErrorCode,
  AIEntityContext,
  AIResourceRef,
} from "@k8s-dashboard/contracts";
import { streamAIChat } from "@/api/aiChat";

/**
 * 대화 상태의 소유자 (ADR 0026 기능 4 · Issue #18)
 * --------------------------------------------------------------------------
 * 상태가 **화면보다 오래 삽니다.** 라우트 컴포넌트가 들고 있으면, 답변 안의
 * 딥링크를 눌러 Pod 상세로 이동하는 순간 패널이 언마운트되고 방금 나눈 대화가
 * 사라집니다 — 그런데 그 이동은 Agent가 하라고 알려준 바로 그 행동입니다.
 *
 * 그렇다고 아무 이동에서나 대화를 이어가면 안 됩니다. 사용자가 좌측 nav로 전혀
 * 다른 워크로드에 갔는데 이전 대화가 따라오면, 화면이 말하는 대상과 대화가 말하는
 * 대상이 어긋납니다. 그래서 이어가는 조건을 **서버가 확인한 참조**로 한정합니다.
 *
 *   이어감   새 화면의 UID가 (a) 이 대화가 시작된 대상이거나 (b) 이 대화의 답변이
 *            실제로 돌려준 참조 중 하나일 때. (b)의 목록은 서버가 조회에 성공한
 *            대상만 담으므로 모델이 지어낼 수 없습니다.
 *   새로 시작 그 밖의 모든 이동.
 *
 * 남은 한계는 정직하게 적습니다 — 이 저장소는 **탭 하나의 메모리**입니다.
 * 새로고침·새 탭에서는 대화가 사라지고, 서버의 세션은 남아 있지만 화면은 그것을
 * 다시 붙이지 않습니다. 재생 버퍼가 프로세스 로컬인 것과 같은 성질입니다.
 */

export type AgentPhase = "idle" | "streaming" | "done" | "error";

export interface AgentMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  references?: AIResourceRef[];
  truncated?: boolean;
  /**
   * 이 답변과 함께 온 제안. (ADR 0027 Phase B)
   *
   * **지금 그려지는 메시지에만 붙습니다.** 별도의 action 저장소를 만들지 않고,
   * 원문 payload는 어디에도 남기지 않으며, 메시지가 상한에서 밀려나면 제안도 함께
   * 사라집니다 — 제안의 수명은 그것을 낳은 답변의 수명입니다.
   */
  actions?: AIActionProposal[];
  /**
   * 승인 가능한 제안의 **서버 발급 레코드 id**. (ADR 0028 Phase C)
   *
   * 제안과 같은 수명입니다 — 메시지가 상한에서 밀려나면 함께 사라집니다.
   * 비어 있으면 카드는 Phase B 그대로 제안 전용입니다.
   */
  approvals?: AIActionApprovalRef[];
}

/** 화면에 남기는 대화 길이 상한. 브라우저 메모리도 유계여야 합니다. */
export const MAX_RENDERED_MESSAGES = 40;

/** reachable 집합의 상한. 긴 대화가 브라우저 메모리를 무한히 늘리지 않습니다. */
export const MAX_REACHABLE = 200;

/**
 * 이어감을 판정하는 **합성 신원**입니다.
 *
 * UID 하나로는 부족합니다 — Kubernetes UID는 클러스터 안에서만 유일하고, 멀티
 * 클러스터 화면에서는 같은 문자열이 다른 대상을 가리킬 수 있습니다. 네임스페이스도
 * 함께 봅니다: 참조가 가리킨 곳과 화면이 선 곳이 다르면 대화가 말하는 대상이
 * 어긋납니다. 종류(pod/workload)까지 넣어 서로 다른 축을 섞지 않습니다.
 *
 * 구분자는 계약이 허용하지 않는 문자입니다(`isSafeResourceRef`는 영숫자와 `._-`만
 * 통과시킵니다) — 값에 따라 경계가 흐려지면 다른 대상이 같은 키가 됩니다.
 */
function identityKey(clusterId: string, namespace: string, kind: "pod" | "workload", uid: string): string {
  return [clusterId, namespace, kind, uid].join("|");
}

/** 화면 맥락의 신원. 종류는 **이름이 아니라** podUid/workloadUid 중 무엇이 왔는지로 정합니다. */
function identityOf(entity: AIEntityContext): string {
  const namespace = entity.namespace ?? "";
  if (entity.podUid) return identityKey(entity.clusterId, namespace, "pod", entity.podUid);
  if (entity.workloadUid) return identityKey(entity.clusterId, namespace, "workload", entity.workloadUid);
  return "";
}

/** 참조의 신원. 참조가 **스스로 들고 있는** cluster·namespace·kind·uid만 씁니다. */
function referenceIdentity(reference: AIResourceRef): string {
  return identityKey(reference.clusterId, reference.namespace, reference.kind, reference.uid);
}

function entityKey(entity: AIEntityContext): string {
  /* UID가 없는 화면도 서로 구분되어야 합니다 — 그때는 위치만으로 키를 만듭니다. */
  return identityOf(entity) || identityKey(entity.clusterId, entity.namespace ?? "", "workload", "");
}

/** 새 신원을 더하되 상한을 지킵니다. 오래된 것부터 빠집니다. */
function withReachable(reachable: Set<string>, identities: string[]): Set<string> {
  const next = new Set(reachable);
  for (const identity of identities) {
    if (identity === "") continue;
    next.delete(identity);
    next.add(identity);
  }
  if (next.size <= MAX_REACHABLE) return next;
  return new Set([...next].slice(next.size - MAX_REACHABLE));
}

interface Conversation {
  sessionId?: string;
  messages: AgentMessage[];
  phase: AgentPhase;
  step: "reading" | "thinking" | "answering" | null;
  error: AIChatErrorCode | null;
  retryAfterSeconds?: number;
  errorEpisode: string | null;
  /** 이 대화를 이어가도 되는 화면의 신원. 시작 대상 + 서버가 확인한 참조입니다. */
  reachable: Set<string>;
  boundKey: string;
}

function freshConversation(entity: AIEntityContext): Conversation {
  const reachable = new Set<string>();
  const identity = identityOf(entity);
  if (identity) reachable.add(identity);
  return {
    messages: [],
    phase: "idle",
    step: null,
    error: null,
    errorEpisode: null,
    reachable,
    boundKey: entityKey(entity),
  };
}

interface AgentChatStore {
  conversation: Conversation;
  /** 패널이 열려 있는지. 대화와 함께 이동을 견딥니다. */
  open: boolean;
  setOpen(next: boolean): void;
  /** 화면이 바뀔 때 대화를 이어갈지 새로 시작할지 정합니다. */
  bind(entity: AIEntityContext): void;
  ask(entity: AIEntityContext, question: string): void;
  stop(): void;
  retry(entity: AIEntityContext): void;
}

const AgentChatContext = createContext<AgentChatStore | undefined>(undefined);

export function AgentChatProvider({ children }: { children: ReactNode }) {
  const [conversation, setConversation] = useState<Conversation>(() =>
    freshConversation({ clusterId: "" }),
  );
  const [open, setOpen] = useState(false);
  const abort = useRef<AbortController>();
  const lastQuestion = useRef("");
  const current = useRef(conversation);
  current.current = conversation;

  const patch = useCallback((next: Partial<Conversation>) => {
    setConversation((prev) => ({ ...prev, ...next }));
  }, []);

  /**
   * 화면이 바뀌었을 때 대화를 이어갈지 정합니다.
   *
   * 이어가는 조건은 **서버가 확인한 참조**뿐입니다. 그래야 "Agent가 알려준 곳으로
   * 이동"과 "사용자가 다른 곳으로 이동"이 구분됩니다.
   */
  const bind = useCallback(
    (entity: AIEntityContext) => {
      const key = entityKey(entity);
      const identity = identityOf(entity);
      const conv = current.current;
      if (conv.boundKey === key) return;
      if (identity && conv.reachable.has(identity) && conv.messages.length > 0) {
        // 검증된 참조를 따라온 이동입니다 — 세션과 대화를 그대로 유지합니다.
        patch({ boundKey: key, reachable: withReachable(conv.reachable, [identity]) });
        return;
      }
      abort.current?.abort();
      lastQuestion.current = "";
      setConversation(freshConversation(entity));
      /* 관련 없는 화면입니다 — 대화를 새로 시작하면서 패널도 닫습니다. 열어 둔 채로
         따라다니면 사용자가 연 적 없는 화면에서 빈 패널이 열립니다. */
      setOpen(false);
    },
    [patch],
  );

  const run = useCallback(
    (entity: AIEntityContext, question: string) => {
      abort.current?.abort();
      const controller = new AbortController();
      abort.current = controller;
      lastQuestion.current = question;
      const askId = `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      setConversation((prev) => ({
        ...prev,
        messages: trim([...prev.messages, { id: askId, role: "user", content: question }]),
        phase: "streaming",
        step: "reading",
        error: null,
        errorEpisode: null,
        retryAfterSeconds: undefined,
      }));

      void streamAIChat(
        { question, sessionId: current.current.sessionId, entity },
        {
          onSession: ({ sessionId }) => patch({ sessionId }),
          onProgress: ({ step }) => patch({ step }),
          /* 조각은 화면에 흘리지 않습니다. 완결된 답변 하나만 남깁니다 —
             부분 출력이 대화 기록이 되면 사용자가 잘린 답을 사실로 읽습니다. */
          onMessage: () => patch({ step: "answering" }),
          onFinal: (final) => {
            setConversation((prev) => {
              /* 참조는 자기 좌표를 들고 옵니다 — 현재 화면의 cluster/namespace를
                 빌려 쓰면 다른 클러스터·네임스페이스의 같은 UID가 통과합니다. */
              const reachable = withReachable(
                prev.reachable,
                (final.references ?? []).map(referenceIdentity),
              );
              return {
                ...prev,
                messages: trim([
                  ...prev.messages,
                  {
                    id: final.messageId,
                    role: "assistant",
                    content: final.content,
                    references: final.references,
                    truncated: final.truncated,
                    actions: final.actions,
                    approvals: final.approvals,
                  },
                ]),
                phase: "done",
                step: null,
                reachable,
              };
            });
          },
          onError: (code, retry) =>
            patch({
              error: code,
              retryAfterSeconds: retry,
              errorEpisode: `${code}:${Date.now()}`,
              phase: "error",
              step: null,
            }),
        },
        controller.signal,
      ).then((outcome) => {
        if (outcome === "aborted") patch({ phase: "idle", step: null });
      });
    },
    [patch],
  );

  const ask = useCallback(
    (entity: AIEntityContext, question: string) => {
      const trimmed = question.trim();
      if (trimmed === "" || current.current.phase === "streaming") return;
      run(entity, trimmed);
    },
    [run],
  );

  const stop = useCallback(() => abort.current?.abort(), []);

  const retry = useCallback(
    (entity: AIEntityContext) => {
      if (lastQuestion.current === "" || current.current.phase === "streaming") return;
      /* 같은 질문을 다시 보냅니다. 사용자 메시지를 두 번 그리지 않도록 마지막
         질문 하나를 걷어내고 새로 시작합니다. */
      setConversation((prev) => {
        const last = prev.messages[prev.messages.length - 1];
        return last?.role === "user" ? { ...prev, messages: prev.messages.slice(0, -1) } : prev;
      });
      run(entity, lastQuestion.current);
    },
    [run],
  );

  const value = useMemo<AgentChatStore>(
    () => ({ conversation, open, setOpen, bind, ask, stop, retry }),
    [conversation, open, bind, ask, stop, retry],
  );
  return <AgentChatContext.Provider value={value}>{children}</AgentChatContext.Provider>;
}

/** Provider 밖에서는 undefined입니다 — 호출부가 패널을 그리지 않습니다. */
export function useAgentChatStore(): AgentChatStore | undefined {
  return useContext(AgentChatContext);
}

function trim(messages: AgentMessage[]): AgentMessage[] {
  return messages.length > MAX_RENDERED_MESSAGES ? messages.slice(-MAX_RENDERED_MESSAGES) : messages;
}
